import { apiClient, profileApiClient } from "@/lib/axios";
import {
  ChallengeCampaign,
  ChallengeCampaignWithTotal,
  ChallengeCampaignStatus,
  ChallengeUser,
  ChallengeMetadata,
  GetAllCampaignsResponse,
  WithPagination,
  LeaderboardPeriodType,
  ReferralUser,
} from "@/types";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { SocialUser } from "@/types/social-pay.types";
import { CreateTelegramUser } from "@/components/socials/telegram-login-button";
import { User, LeaderboardUserProfile } from "@/types/profile.types";
import { DevnetEligibility, DevnetLinkedWallet, DevnetEligibilityCriteria } from "@/types/devnet.types";
import { BadgeDefinition, UserBadgesResponse } from "@/types/badges.types";
import {
  ClaimMainnetPrivateBetaDiscordRoleResponse,
  MainnetAllLeaderboardResponse,
  MainnetLeaderboardMeResponse,
  MainnetCampaignsResponse,
  MainnetDashboardResponse,
  MainnetPartnerDetailResponse,
  MainnetPartnerLeaderboardResponse,
  MainnetPrivateBetaEligibility,
  MainnetPrivateBetaEligibilityCriteria,
  MainnetClaimResponse,
  MainnetWalletLinkGroup,
  LinkMainnetWalletRequest,
} from "@/types/mainnet-campaign";
import { MAINNET_CAMPAIGN_CONFIG, PORTAL_WALLET_LINK_CONFIG } from "@/config/campaign";
import type {
  AdminCampaignScope,
  PartnerWhitelistCheck,
} from "@/types/admin.types";

export interface CampaignStats {
  campaign_internal_id: number;
  campaign_id: string;
  campaign_group: string;
  total_users: number;
  total_successful_tasks: string;
  tasks_breakdown: Record<string, number>;
}

// PayID related types
export interface GetPayIdsByAddressRequestDto {
  addresses: string[];
}

export interface PayIdInfo {
  address: string;
  payId: string | null;
}

export interface GetPayIdsByAddressResponseDto {
  success: boolean;
  data: PayIdInfo[];
}

const getDeviceSignature = async () => {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId; // Unique visitor identifier
  } catch (error) {
    console.error("Error fetching device signature:", error);
    return null;
  }
};

export class PulsarService {
  static async getSatsumaSession(token: string) {
    const { data } = await apiClient.get<{ allowed: boolean }>("/portal/mainnet/satsuma/session", {
      headers: this.authHeaders(token),
    });
    return data;
  }

  static async getFunMarketsSession(token: string) {
    const { data } = await apiClient.get<{ allowed: boolean }>("/portal/mainnet/fun-markets/session", {
      headers: this.authHeaders(token),
    });
    return data;
  }

  private static authHeaders(token?: string | null) {
    return token
      ? {
          Authorization: `Bearer ${token}`,
          "x-privy-access-token": token,
        }
      : undefined;
  }

  private static get mainnetCampaignParams() {
    return {
      campaignId: MAINNET_CAMPAIGN_CONFIG.id,
      campaignGroup: MAINNET_CAMPAIGN_CONFIG.groupId,
    };
  }

  private static get portalWalletLinkParams() {
    return {
      campaignId: PORTAL_WALLET_LINK_CONFIG.id,
      campaignGroup: PORTAL_WALLET_LINK_CONFIG.groupId,
    };
  }

  static async getMainnetDashboard(token?: string | null) {
    const { data } = await apiClient.get<MainnetDashboardResponse>(`/portal/mainnet/dashboard`, {
      headers: this.authHeaders(token),
    });
    return data;
  }

  static async getMainnetCampaigns(token?: string | null) {
    const { data } = await apiClient.get<MainnetCampaignsResponse>(`/portal/mainnet/campaigns`, {
      params: this.mainnetCampaignParams,
      ...(token
        ? {
            headers: this.authHeaders(token),
          }
        : {}),
    });
    return data;
  }

  static async getMainnetCampaignDetail(
    partnerId: string,
    token?: string | null,
    scope: { campaignId: string; campaignGroup: string; refresh?: boolean } = this.mainnetCampaignParams,
  ) {
    const { data } = await apiClient.get<MainnetPartnerDetailResponse>(`/portal/mainnet/campaigns/${partnerId}`, {
      params: scope,
      ...(token
        ? {
            headers: this.authHeaders(token),
          }
        : {}),
    });
    return data;
  }

  static async getMainnetLeaderboard(scope: "all" | "reppo", page = 0, limit = 10, token?: string | null) {
    const { data } = await apiClient.get<MainnetAllLeaderboardResponse | MainnetPartnerLeaderboardResponse>(
      `/portal/mainnet/leaderboard`,
      {
        params: { ...this.mainnetCampaignParams, scope, page, limit },
        headers: this.authHeaders(token),
      },
    );
    return data;
  }

  static async getMyMainnetLeaderboardEntry(token?: string | null) {
    const { data } = await apiClient.get<MainnetLeaderboardMeResponse>(
      `/portal/mainnet/leaderboard/me`,
      {
        params: this.mainnetCampaignParams,
        headers: this.authHeaders(token),
      },
    );
    return data;
  }

  static async claimMainnetTier(taskGuid: string, token?: string | null) {
    const { data } = await apiClient.post<MainnetClaimResponse>(
      `/portal/mainnet/claim`,
      { taskGuid },
      token
        ? {
            headers: this.authHeaders(token),
          }
        : undefined,
    );
    return data;
  }

  static async getMainnetPrivateBetaEligibility(token?: string | null) {
    const { data } = await apiClient.get<MainnetPrivateBetaEligibility>(`/portal/mainnet/private-beta/eligibility`, {
      headers: this.authHeaders(token),
    });
    return data;
  }

  static async getMainnetPrivateBetaEligibilityCriteria() {
    const { data } = await apiClient.get<MainnetPrivateBetaEligibilityCriteria>(
      `/portal/mainnet/private-beta/eligibility-criteria`,
    );
    return data;
  }

  static async claimMainnetPrivateBetaDiscordRole(token?: string | null) {
    const { data } = await apiClient.post<ClaimMainnetPrivateBetaDiscordRoleResponse>(
      `/portal/mainnet/private-beta/claim-discord-role`,
      undefined,
      { headers: this.authHeaders(token) },
    );
    return data;
  }

  static async getMainnetLinkedWallets(token?: string | null) {
    const { data } = await apiClient.get<MainnetWalletLinkGroup | null>(`/portal/mainnet/linked-wallets`, {
      params: this.portalWalletLinkParams,
      headers: this.authHeaders(token),
    });
    return data;
  }

  static async linkMainnetWallet(payload: LinkMainnetWalletRequest, token?: string | null) {
    const { data } = await apiClient.post<MainnetWalletLinkGroup>(`/portal/mainnet/linked-wallets`, payload, {
      params: this.portalWalletLinkParams,
      headers: this.authHeaders(token),
    });
    return data;
  }

  static async switchMainnetPrimaryWallet(walletAddress: string, token?: string | null) {
    const { data } = await apiClient.post<MainnetWalletLinkGroup>(
      `/portal/mainnet/linked-wallets/switch-primary`,
      { walletAddress },
      {
        params: this.portalWalletLinkParams,
        headers: this.authHeaders(token),
      },
    );
    return data;
  }

  static async checkPartnerWhitelist(
    params: AdminCampaignScope & { walletAddress: string },
  ) {
    const { data } = await apiClient.get<PartnerWhitelistCheck>(
      `/portal/mainnet/whitelist/check`,
      {
        params,
      },
    );
    return data;
  }

  static async getChallengeCampaigns() {
    const { data } = await apiClient.get<ChallengeCampaignWithTotal[]>(`/challenges`);
    return data;
  }

  static async getChallengeCampaign(id: string, groupId?: string) {
    const url = groupId ? `/challenges/${id}/${groupId}` : `/challenges/${id}`;
    const { data } = await apiClient.get<ChallengeCampaign>(url);
    return data;
  }

  static async getChallengeCampaignTaskStatus(id: string, groupId?: string, token?: string | null) {
    const url = groupId ? `/challenges/${id}/tasks-status/${groupId}` : `/challenges/${id}/tasks-status`;
    const { data } = await apiClient.get<ChallengeCampaignStatus>(url, {
      headers: this.authHeaders(token),
    });
    return data;
  }

  static async getChallengeCampaignLeaderboard(
    campaignId: string,
    groupId: string | undefined,
    page: number,
    limit: number,
    period: LeaderboardPeriodType = LeaderboardPeriodType.ALL,
    date?: string,
  ) {
    let url = `/challenges/${campaignId}/leaderboard?page=${page}&limit=${limit}`;
    if (groupId) {
      url = `/challenges/${campaignId}/leaderboard/${groupId}?page=${page}&limit=${limit}`;
    }
    if (period !== LeaderboardPeriodType.ALL) {
      url += `&type=${period}`;
    }
    if (date) {
      url += `&date=${date}`;
    }
    const { data } = await apiClient.get<WithPagination<ChallengeUser>>(url);
    return data;
  }

  static async getChallengeCampaignUser(campaignId: string, groupId?: string, token?: string | null) {
    const results = await Promise.allSettled([getDeviceSignature()]);
    const deviceSignature = results[0].status === "fulfilled" ? results[0].value : undefined;

    const url = groupId ? `/challenges/${campaignId}/me/${groupId}` : `/challenges/${campaignId}/me`;

    const { data } = await apiClient.get<ChallengeUser>(url, {
      headers: {
        ...(deviceSignature ? { "X-Device-Signature": deviceSignature } : {}),
        ...(this.authHeaders(token) ?? {}),
      },
    });
    return data;
  }

  static async getChallengeCampaignMetadata(id: string, groupId?: string) {
    const url = groupId ? `/challenges/${id}/metadata/${groupId}` : `/challenges/${id}/metadata`;
    const { data } = await apiClient.get<ChallengeMetadata>(url);
    return data;
  }

  static async getAllCampaigns() {
    const { data } = await apiClient.get<GetAllCampaignsResponse>(`/challenges/all`);
    return data;
  }

  static async getAllCampaignsByGroup(group: string) {
    const { data } = await apiClient.get<GetAllCampaignsResponse>(`/challenges/all/${group}`);
    return data;
  }

  static async getCampaign(campaignId: string, groupId?: string) {
    const url = groupId ? `/challenges/${campaignId}/${groupId}` : `/challenges/${campaignId}`;
    const { data } = await apiClient.get<ChallengeCampaign>(url);
    return data;
  }

  static doCampaignTask = async (taskGuid: string, extraArguments?: string[], token?: string | null) => {
    return apiClient.post<{ status: boolean; error?: string }>(
      `/challenges/do-task`,
      { taskGuid, extraArguments: extraArguments || [] },
      { headers: this.authHeaders(token) },
    );
  };

  static createReferral = async (campaignId: string, groupId: string | undefined, referralCode: string) => {
    if (groupId) {
      return apiClient.post<{ success: boolean }>(`/challenges/${campaignId}/create-referral/${groupId}`, { referralCode });
    }
    return apiClient.post<{ success: boolean }>(`/challenges/${campaignId}/create-referral`, { referralCode });
  };

  static getSocialUser = async (token?: string | null) => {
    return apiClient.get<SocialUser>(`/social-pay/me`, {
      headers: this.authHeaders(token),
    });
  };

  static registerTwitter = async (redirectUrl?: string, token?: string | null) => {
    return apiClient.post<string>("/social-pay/register/twitter", {
      type: "register",
      redirectUrl,
    }, {
      headers: this.authHeaders(token),
    });
  };

  static registerTelegram = async (body: CreateTelegramUser, token?: string | null) => {
    return apiClient.post<string>("/social-pay/register/telegram", body, {
      headers: this.authHeaders(token),
    });
  };

  static unlinkTelegram = async (token?: string | null) => {
    return apiClient.delete<string>("/social-pay/register/telegram", {
      headers: this.authHeaders(token),
    });
  };

  static unlinkTwitter = async (token?: string | null) => {
    return apiClient.delete<string>("/social-pay/unregister", {
      headers: this.authHeaders(token),
    });
  };

  static getProfile = async () => {
    return profileApiClient.get<User | undefined>(`/users`);
  };

  static checkUsernameValid = async (username: string) => {
    return profileApiClient.get<{ valid: boolean }>(`/users/valid-username/${encodeURIComponent(username)}`);
  };

  static updateProfile = async (formData: FormData) => {
    return profileApiClient.put<User>(`/users`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  static linkAccount = async (email: string) => {
    return profileApiClient.post(`/users/link-email`, { email });
  };

  static verifyEmail = async (code: string) => {
    return profileApiClient.post(`/users/verify-email`, { code });
  };

  static resendVerificationCode = async (email: string) => {
    return profileApiClient.post(`/users/resend-verification`, { email });
  };

  static unlinkAccount = async () => {
    return profileApiClient.post(`/users/unlink-address`);
  };

  static registerDiscord = async (redirectUrl: string, token?: string | null) => {
    return apiClient.get<string>(`/social-pay/register/discord?redirectUri=${encodeURIComponent(redirectUrl)}`, {
      headers: this.authHeaders(token),
    });
  };

  static unregisterDiscord = async (token?: string | null) => {
    return apiClient.delete("/social-pay/register/discord", {
      headers: this.authHeaders(token),
    });
  };

  static getUserProfiles = async (addresses: string[]) => {
    const joined = addresses.join(",");
    const { data } = await profileApiClient.get<Record<string, LeaderboardUserProfile>>(`/challenges/user-profiles/${joined}`);
    return data;
  };

  // Game Leaderboard methods
  static async getGameLeaderboard(period: string, limit: number = 100) {
    const { data } = await apiClient.get(`/game-leaderboard`, {
      params: { period, limit },
    });
    return data;
  }

  static async getGameLeaderboardByTps(period: string, limit: number = 100) {
    const { data } = await apiClient.get(`/game-leaderboard/by-tps`, {
      params: { period, limit },
    });
    return data;
  }

  static async getGameLeaderboardByScore(period: string, limit: number = 100) {
    const { data } = await apiClient.get(`/game-leaderboard/by-score`, {
      params: { period, limit },
    });
    return data;
  }

  static async getReferrals(campaignId: string, groupId: string | undefined, address: string, page: number, limit: number) {
    let url = `/challenges/${campaignId}`;
    if (groupId) {
      url += `/${groupId}`;
    }
    url += `/referrals?page=${page}&limit=${limit}`;
    // The address is sent as a header (like other endpoints that require wallet context)
    const response = await apiClient.get<{
      count: number;
      data: ReferralUser[];
    }>(url, {
      headers: {
        "X-User-Address": address,
      },
    });
    return response.data;
  }

  static async getCampaignStats(campaignId: string, groupId: string) {
    const url = `/challenges/${campaignId}/${groupId}/stats`;
    const { data } = await apiClient.get<CampaignStats>(url);
    return data;
  }

  // --- Devnet Campaign Endpoints ---

  static async checkDevnetEligibility(): Promise<DevnetEligibility> {
    const { data } = await apiClient.get<DevnetEligibility>(`/devnet/eligibility`);
    return data;
  }

  static async getDevnetEligibilityCriteria(): Promise<DevnetEligibilityCriteria> {
    const { data } = await apiClient.get<DevnetEligibilityCriteria>(`/devnet/eligibility-criteria`);
    return data;
  }

  static async getDevnetLinkedWallet(): Promise<DevnetLinkedWallet | null> {
    const { data } = await apiClient.get<DevnetLinkedWallet | null>(`/devnet/linked-wallet`);
    return data;
  }

  static async linkDevnetWallet(signature: string, message: string): Promise<DevnetLinkedWallet> {
    const { data } = await apiClient.post<DevnetLinkedWallet>(`/devnet/link-wallet`, { signature, message });
    return data;
  }

  static async unlinkDevnetWallet(): Promise<void> {
    await apiClient.delete(`/devnet/linked-wallet`);
  }

  // --- Badge Endpoints ---

  static async getBadgeDefinitions() {
    const { data } = await apiClient.get<BadgeDefinition[]>(`/challenges/badges/definitions`);
    return data;
  }

  static async getMyBadges() {
    const { data } = await apiClient.get<UserBadgesResponse>(`/challenges/badges/me`);
    return data;
  }

  static async getUserBadges(address: string) {
    const { data } = await apiClient.get<UserBadgesResponse>(`/challenges/badges/user/${address}`);
    return data;
  }
}
